import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import './index.css';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
import UpdatePromptPopup from './components/UpdatePromptPopup';

// Scroll restoration component
const ScrollToTop = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Force scroll to top on every route change
    window.scrollTo(0, 0);
    // Also try to reset any scrollable elements
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, [location]);

  return null;
};

// Custom Router wrapper to handle scroll behavior
const CustomRouter = ({ children }) => {
  return (
    <Router>
      <ScrollToTop />
      {children}
    </Router>
  );
};

// Définir l'objet theme
const theme = {
  colors: {
    primary: 'var(--primary-color)',
    secondary: 'var(--secondary-color)',
    background: 'var(--background-color)',
    text: 'var(--text-color)',
    lightText: 'var(--light-text-color)',
    lightGray: 'var(--light-gray-color)',
    veryLightGray: 'var(--very-light-gray-color)',
    mediumGray: 'var(--medium-gray-color)',
  },
};

// Composant pour gérer les mises à jour du service worker et l'installation de l'app
function ServiceWorkerHandler() {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [isReady, setIsReady] = useState(false);

  // Attendre que React soit complètement chargé avant de vérifier les mises à jour
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 2000); // Attendre 2 secondes après le chargement initial
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isReady) return; // Ne pas vérifier avant que l'app soit prête
    
    // Vérifier si on vient juste de faire une mise à jour
    const justUpdated = localStorage.getItem('sw_just_updated');
    if (justUpdated) {
      const updateTime = parseInt(justUpdated, 10);
      const now = Date.now();
      // Si la mise à jour date de moins de 10 secondes, ne pas vérifier
      if (now - updateTime < 10000) {
        console.log('[SW Handler] Mise à jour récente détectée, skip de la vérification');
        return;
      }
      // Sinon, nettoyer le flag
      localStorage.removeItem('sw_just_updated');
    }
    
    // Fonction pour vérifier s'il y a un SW en attente
    const checkForWaitingServiceWorker = (registration) => {
      if (registration.waiting) {
        console.log('[SW Handler] Service worker en attente détecté immédiatement');
        setWaitingWorker(registration.waiting);
        setNewVersionAvailable(true);
      }
    };

    // Écouter les messages du service worker
    const messageHandler = (event) => {
      if (event.data && event.data.type === 'NEW_VERSION') {
        console.log('[SW Handler] Nouvelle version détectée par le service worker.');
        setNewVersionAvailable(true);
      }
    };
    
    // Écouter l'événement personnalisé quand un nouveau SW est prêt
    const swWaitingHandler = (event) => {
      console.log('[SW Handler] Nouveau service worker en attente (via event)');
      setWaitingWorker(event.detail);
      setNewVersionAvailable(true);
    };
    
    navigator.serviceWorker.addEventListener('message', messageHandler);
    window.addEventListener('sw-waiting', swWaitingHandler);
    
    // Vérifier immédiatement s'il y a un SW en attente
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration) {
          console.log('[SW Handler] Registration trouvée:', {
            installing: registration.installing ? 'yes' : 'no',
            waiting: registration.waiting ? 'yes' : 'no',
            active: registration.active ? 'yes' : 'no'
          });
          checkForWaitingServiceWorker(registration);
        }
      });
    }
    
    return () => {
      navigator.serviceWorker.removeEventListener('message', messageHandler);
      window.removeEventListener('sw-waiting', swWaitingHandler);
    };
  }, [isReady]);

  // Fonction pour appliquer la mise à jour manuellement
  const applyUpdate = () => {
    console.log('[SW Handler] User requested update, sending SKIP_WAITING message');
    
    // Marquer qu'on vient de faire une mise à jour
    localStorage.setItem('sw_just_updated', Date.now().toString());
    
    // Envoyer le message SKIP_WAITING au service worker en attente
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    } else if (navigator.serviceWorker.controller) {
      // Fallback: envoyer au controller actuel
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
    
    setNewVersionAvailable(false);
  };
  
  // Gérer le controllerchange pour recharger APRÈS que l'utilisateur ait cliqué sur "Mettre à jour"
  useEffect(() => {
    const controllerChangeHandler = () => {
      console.log('[SW Handler] Controller changed, reloading page');
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', controllerChangeHandler);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', controllerChangeHandler);
    };
  }, []);

  // Ne rendre la popup que quand l'app est prête
  if (!isReady) return null;

  // Rendu du composant avec la popup de mise à jour
  return (
    <UpdatePromptPopup 
      isVisible={newVersionAvailable}
      onUpdate={applyUpdate}
    />
  );
}

const root = createRoot(document.getElementById('root'));

root.render(
  <CustomRouter>
    <ThemeProvider theme={theme}>
      <AuthProvider>
        {/* Inclure le gestionnaire de service worker au niveau supérieur */}
        <ServiceWorkerHandler />
        <App />
      </AuthProvider>
    </ThemeProvider>
  </CustomRouter>
);

// Enregistrer le service worker avec une meilleure gestion des mises à jour
if ('serviceWorker' in navigator) {
  // Prevent multiple registration attempts which can cause issues
  let swRegistration = null;
  let registrationAttempts = 0;
  const MAX_REGISTRATION_ATTEMPTS = 3;
  
  const registerServiceWorker = () => {
    if (registrationAttempts >= MAX_REGISTRATION_ATTEMPTS) {
      console.warn('Maximum service worker registration attempts reached, skipping registration');
      return;
    }
    
    registrationAttempts++;
    
    // Check if we've already registered
    if (swRegistration && swRegistration.active) {
      console.log('Service worker already registered and active');
      return;
    }
    
    console.log(`Attempting service worker registration (attempt ${registrationAttempts})`);
    
    navigator.serviceWorker
      .register('/service-worker.js')
      .then(registration => {
        swRegistration = registration;
        console.log('Service Worker enregistré avec succès:', registration.scope);
        
        // Store the registration timestamp to prevent rapid re-registrations
        localStorage.setItem('sw_registration_time', Date.now().toString());
        
        // Vérifier régulièrement les mises à jour du service worker
        const updateInterval = setInterval(() => {
          if (navigator.onLine) {
            registration.update()
              .then(() => console.log('Vérification des mises à jour du service worker effectuée'))
              .catch(err => console.warn('Erreur lors de la vérification des mises à jour du SW:', err));
          }
        }, 60 * 60 * 1000); // Vérifier toutes les heures

        // Gérer les mises à jour
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log('[SW Registration] Nouveau service worker en installation...');
          
          newWorker.addEventListener('statechange', () => {
            console.log('[SW Registration] État du service worker:', newWorker.state);
            
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[SW Registration] Nouveau service worker installé, notification à l\'utilisateur');
              // Notifier l'application qu'un nouveau SW est prêt
              window.dispatchEvent(new CustomEvent('sw-waiting', { detail: newWorker }));
            }
          });
        });
        
        // Clean up the interval if needed
        window.addEventListener('unload', () => {
          clearInterval(updateInterval);
        });
      })
      .catch(error => {
        console.error('Échec de l\'enregistrement du service worker:', error);
        
        // Store the error to prevent loops
        localStorage.setItem('sw_registration_error', Date.now().toString());
        
        // Only retry registration after a delay and if this isn't a persistent error
        const lastErrorTime = parseInt(localStorage.getItem('sw_registration_error') || '0', 10);
        const now = Date.now();
        
        // If it's been more than 10 minutes since the last error, try again
        if (now - lastErrorTime > 10 * 60 * 1000 && registrationAttempts < MAX_REGISTRATION_ATTEMPTS) {
          setTimeout(registerServiceWorker, 30000); // Try again in 30 seconds
        }
      });
  };
  
  // Toujours enregistrer le service worker pour permettre la détection des mises à jour
  // Le navigateur gère lui-même les vérifications et évite les enregistrements inutiles
  window.addEventListener('load', () => {
    // Give the page time to fully load before registering the SW
    setTimeout(registerServiceWorker, 1000);
  });

  // // Gérer les changements de contrôleur du service worker
  // navigator.serviceWorker.addEventListener('controllerchange', () => {
  //   console.log('Le service worker a changé, la page va se recharger pour appliquer les mises à jour');
    
  //   // Prevent infinite reload loops by checking when the last reload happened
  //   const lastReloadTime = parseInt(localStorage.getItem('sw_last_reload') || '0', 10);
  //   const now = Date.now();
    
  //   // If we haven't reloaded in the last minute, it's safe to reload
  //   if (now - lastReloadTime > 60 * 1000) {
  //     localStorage.setItem('sw_last_reload', now.toString());
      
  //     // Optional: only reload if the page has been loaded for more than 5 minutes
  //     // This prevents reloads during the initial session
  //     const pageLoadTime = parseInt(localStorage.getItem('page_load_time') || '0', 10);
  //     if (pageLoadTime === 0) {
  //       localStorage.setItem('page_load_time', now.toString());
  //     } else if (now - pageLoadTime > 5 * 60 * 1000) {
  //       window.location.reload();
  //     }
  //   } else {
  //     console.warn('Prevented potential reload loop - last reload was too recent');
  //   }
  // });
}